/**
 * The cast TRANSPORT, receiver side: a PCM WebSocket from a SYQEL sender → the jitter-buffer worklet → the
 * analysers (and, on a TV, the speakers). The socket runs in a Worker (pcm-socket-worker.js) that feeds the
 * worklet over its own MessagePort; the worklet reports the playout clock and quiet runs to cast/session.ts,
 * and the control channel's text frames go to cast/control.ts.
 */
import { closeCtx } from '../audio/graph'
import { createProcessorNode } from '../processorNode'
import { scriptUrl } from '../publicUrl'
import { receiverHello } from '../receiverIdentity'
import { onControlText } from './control'
import { castOnGap, castOnPos, endSenderSession } from './session'

// ── REMOTE LAN AUDIO (the TV receiver) — connect to a PCM WebSocket served by a SYQEL sender
// (or by the native TV shell re-serving the LAN stream on localhost). Same wire format as the
// desktop native path: f32-interleaved-stereo @ 48 kHz binary frames, control rides text frames.
// UNLIKE the desktop/web analysis-only paths, the TV is the SPEAKER, so by default the stream is
// AUDIBLE (node → gain → destination) and the analyser taps the same node, keeping sound + visuals
// locked. The session that dialled takes the pipe over on open (PcmOwner.adopt) — the same slots and
// stopNative() teardown as captureNative.
export interface RemoteOptions {
  sampleRate?: number
  channels?: number
  /** true (default) = the TV plays the sound; false = a silent analysis tap (visuals-only). */
  audible?: boolean
  /** Jitter-buffer cushion override (ms). The relayed PUSH path uses a deeper cushion (300) than a
   *  direct pull (180) — the extra relay hop adds burstiness a shallow cushion turns into chop. */
  targetMs?: number
  /** Grow the cushion on underrun (default true — network jitter). A LOOPBACK feed has no network:
   *  its underruns are track starts / app stalls, so it holds its cushion and re-primes instead. */
  adaptive?: boolean
  /**
   * Hardware output buffer to ask the platform for, in seconds (AudioContext latencyHint as a number).
   * The TV is the speaker AND a weak, GPU-bound renderer: its audio render thread measurably stops
   * keeping real time while the main thread compiles a world (15–20% starvation, glitches no jitter
   * buffer can see). A larger output buffer is the slack that rides those pauses out. On the LG webOS
   * set the named hints both yield 2048 frames (43 ms); a numeric 0.25 yields 8192 (171 ms).
   */
  outputBufferSec?: number
  name?: string
  /** fired when the socket closes after a successful connect — the UI returns to the pairing screen. */
  onClose?: () => void
  /**
   * Fired when PCM actually starts ARRIVING — the first frame, and again whenever frames resume after
   * a stall — i.e. audio is really flowing, not merely that a socket opened. A socket can open and then
   * carry nothing (a sender in phone-only mode, an idle desktop app, a stalled capture), and reporting
   * that as "connected" is a lie the user reads as a broken TV. UIs should show their live/casting
   * state from THIS (+ onAudioStop), and use the connect resolve only to stop retrying.
   */
  onAudio?: () => void
  /** Fired when frames have STOPPED arriving for 5 s on an open socket (pcm-socket-worker FLOW — the
   *  debounce rides out track changes and relay hiccups). onAudio fires again when they resume. */
  onAudioStop?: () => void
  /** What this receiver tells the sender it dialled (a TV's offer — receiver/offer.ts). */
  upstream?: Upstream
}

/**
 * A state a receiver keeps the sender told of, over the same socket (text frames, receiver → sender): sent once
 * the socket opens, then again — whole — whenever `subscribe` says it may have changed and the frame really
 * did, until the socket closes.
 */
export interface Upstream {
  frame: () => string
  subscribe: (onChange: () => void) => () => void
}

/** Who a connect belongs to — the audio session that dialled it (audioSession: captureRemote / startCast). */
export interface PcmOwner {
  /** Is this connect still the session's current source? (A newer source supersedes it.) */
  current: () => boolean
  /** The socket opened: the session takes the pipe over — its worker and context are the session's to stop
   *  from now on (and `release` with them: the pipe's own teardown), its node the source both listeners
   *  hear, its label the source's name. */
  adopt: (pipe: { worker: Worker; ctx: AudioContext; node: AudioNode; label: string; release: () => void }) => void
}

/**
 * Connect to a PCM WebSocket and feed it into the worklet → analyser. Does NOT tear down prior state
 * (the caller does that ONCE) — so startCast can attach a second, local consumer to its own cast over
 * localhost without the teardown killing the cast. Resolves true on open, false on failure.
 */
export async function connectPcm(wsUrl: string, opts: RemoteOptions, owner: PcmOwner): Promise<boolean> {
  // SESSION GUARD: captureRemote bumps `session` before calling us. If a newer connect starts while
  // this one is still dialling (a push preempting an in-flight pull, two injects in one tick), the
  // newer one's stopNative() finds nothing to stop — we only register on 'open' — so without this
  // check the LOSER would open later, overwrite the registration and leave the winner's context
  // orphaned (seen on the LG TV: a push's first frame arriving into a CLOSED context = silence).
  const sampleRate = opts.sampleRate ?? 48_000
  const channels = opts.channels ?? 2
  const audible = opts.audible ?? true
  // match the wire rate — no resampling; a numeric latencyHint when the caller wants output-buffer slack
  const ctx = new AudioContext(opts.outputBufferSec ? { sampleRate, latencyHint: opts.outputBufferSec } : { sampleRate })
  let lastStatFrame = -1 // audio-clock bookkeeping for the stat log (see below)
  let lastStatAtMs = 0
  // LAN cast: 150ms cushion to absorb Wi-Fi jitter while keeping latency bounded (the worklet's
  // jitter buffer hard-skips if it ever drifts higher, so the delay can't accumulate).
  const node = await createProcessorNode(ctx, { script: '/native-pcm-worklet.js', name: 'native-pcm', numberOfInputs: 0, outputChannels: 2, processorOptions: { targetMs: opts.targetMs ?? 180, adaptive: opts.adaptive ?? true } })
  if (!owner.current()) { closeCtx(ctx); return false } // superseded during the module load
  node.port.onmessage = (e) => {
    if (e.data && e.data.type === 'pos') castOnPos(e.data, ctx) // castProtocol playout clock (on the audio clock)
    else if (e.data && e.data.type === 'gap') castOnGap(e.data.sec as number) // a quiet run of the played stream
    else if (e.data && e.data.type === 'stat') {
      // jitter-buffer telemetry: filtered occupancy vs target, the steering ratio (ppm — a real clock
      // drift shows up here as a steady value), the only deliberate discontinuities (resyncs),
      // concealed ms (underruns), and the arrival rate the worklet actually saw.
      const d = e.data as { audioFrame?: number; latencyMs: number; occMs?: number; targetMs?: number; primed: boolean; ratioPpm?: number; arrivalPpm?: number; resyncs?: number; grows?: number; underMs: number; fedFrames?: number; feedMsgs?: number }
      // AUDIO CLOCK vs WALL CLOCK: how many audio-seconds the worklet rendered per wall-second. A
      // steady value ≠ 1 is the device's output clock offset (the converter absorbs it); a value that
      // wanders is the render thread missing quanta — glitches no buffer counter can see.
      const nowMs = performance.now()
      let clock = ''
      if (typeof d.audioFrame === 'number' && lastStatFrame >= 0 && nowMs > lastStatAtMs) {
        clock = ` clock=${(((d.audioFrame - lastStatFrame) / sampleRate) / ((nowMs - lastStatAtMs) / 1000)).toFixed(4)}`
      }
      if (typeof d.audioFrame === 'number') { lastStatFrame = d.audioFrame; lastStatAtMs = nowMs }
      console.log(`[syqel·cast] buffer ~${d.latencyMs}ms/${d.targetMs ?? '?'} ${d.primed ? '' : '(priming) '}ratio=${d.ratioPpm ?? 0}ppm arrival=${d.arrivalPpm ?? 0}ppm resyncs=${d.resyncs ?? 0} grows=${d.grows ?? 0} under=${d.underMs}ms fed=${d.fedFrames ?? 0}f/${d.feedMsgs ?? 0}msg${clock}`)
    }
  }
  const gain = ctx.createGain()
  gain.gain.value = audible ? 1 : 0 // audible: this device is the speaker; silent: analysis tap only
  node.connect(gain)
  gain.connect(ctx.destination)
  await ctx.resume()
  // autoplay policy can leave the context SUSPENDED — then the worklet never runs (no analysis, no
  // sound) even though the socket is connected. Resume on the next interaction as a fallback (a click
  // on the desktop, a remote keypress on the TV).
  if (ctx.state !== 'running') {
    const rescue = (): void => {
      void ctx.resume()
      window.removeEventListener('pointerdown', rescue)
      window.removeEventListener('keydown', rescue)
    }
    window.addEventListener('pointerdown', rescue)
    window.addEventListener('keydown', rescue)
  }
  // Run the socket in a Worker and feed PCM to the worklet over a dedicated MessagePort, so a janky
  // WebGL render frame on a weak TV box can't stall ingestion and drain the jitter buffer (→ clicks).
  const worker = new Worker(await scriptUrl('/pcm-socket-worker.js'))
  const feed = new MessageChannel()
  node.port.postMessage({ type: 'feedPort', channels }, [feed.port2])
  console.log('[syqel·cast] connecting', wsUrl, '· ctx.state =', ctx.state, '· outputBuffer =', Math.round(ctx.baseLatency * 1000) + 'ms')
  return await new Promise<boolean>((resolve) => {
    let settled = false
    let timer = 0
    // the upstream state, while the socket is open (see Upstream)
    let lastUp = ''
    let stopUp: (() => void) | null = null
    const sendUp = (): void => {
      const up = opts.upstream
      if (!up) return
      const frame = up.frame()
      if (frame === lastUp) return
      lastUp = frame
      worker.postMessage({ type: 'send', data: frame })
    }
    const endUp = (): void => { stopUp?.(); stopUp = null }
    // resolve false on any pre-open failure so the caller's retry kicks in instead of hanging forever
    // (worker failed to load, bad URL, sender unreachable, or no frames within the timeout).
    const fail = (): void => {
      if (settled) return
      settled = true
      window.clearTimeout(timer)
      worker.terminate()
      closeCtx(ctx)
      resolve(false)
    }
    timer = window.setTimeout(fail, 8000)
    worker.onerror = fail
    worker.onmessage = (e: MessageEvent) => {
      const d = e.data
      if (d?.type === 'open') {
        if (!owner.current()) { fail(); return } // superseded while dialling — never register
        window.clearTimeout(timer)
        endSenderSession() // fresh cast session — new stream clock, nothing of the previous sender survives
        owner.adopt({ worker, ctx, node, label: opts.name || 'LAN audio', release: endUp })
        console.log('[syqel·cast] ws OPEN', wsUrl)
        if (opts.upstream) { sendUp(); stopUp = opts.upstream.subscribe(sendUp) }
        settled = true
        resolve(true)
      } else if (d?.type === 'firstframe') {
        // AUDIO-READY — distinct from socket-open above. Only now is the cast genuinely live.
        console.log('[syqel·cast] first PCM frame', d.len, '· ctx.state =', ctx.state)
      } else if (d?.type === 'flow') {
        if (d.on) opts.onAudio?.()
        else { console.log('[syqel·cast] no PCM for 5 s — the sender is connected but idle'); opts.onAudioStop?.() }
      } else if (d?.type === 'close' || d?.type === 'error') {
        if (d.type === 'close') { endUp(); endSenderSession() } // sender gone → watermarked, no favorites, own Director
        if (!settled) fail()
        else if (d.type === 'close') opts.onClose?.()
      } else if (d?.type === 'text') {
        // control channel: entitlement announcements + castProtocol messages ride text frames.
        // Wire hygiene: cap the byte size BEFORE JSON.parse — the control channel is untrusted.
        onControlText(d.data)
      }
    }
    // the first frame says who this TV is (a phone that asks its user first names us and remembers the answer)
    worker.postMessage({ type: 'init', url: wsUrl, channels, port: feed.port1, hello: receiverHello() }, [feed.port1])
  })
}
